import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { useContext, useId, useState } from 'react';
import { cn } from '../../../lib/styles';
import { Button } from '../button/button.spark';
import { Heading } from '../heading/heading.spark';
import { Icon } from '../icon/icon.spark';
import { CardBadges, CollapseToggle } from './card.shared';
import { CollapseContext } from './collapse-context';
import type { CardBodyProps, CardHeaderProps, CardProps, CardVariant } from './types';

const VARIANT_MAP: Record<CardVariant, string> = {
  base: 'bg-white',
  secondary: 'bg-gray-100',
};

const SIZE_MAP: Record<NonNullable<CardProps['size']>, string> = {
  md: 'gap-2 px-5 py-4',
  sm: 'gap-1 px-4 py-3',
};

const GAP_MAP: Record<NonNullable<CardProps['size']>, string> = {
  md: 'gap-2',
  sm: 'gap-1',
};

export function Card(props: CardProps) {
  const bodyId = useId();

  const [showBody, setShowBody] = useState<boolean>(!props.initialCollapsed);

  const size = props.size ?? 'md';

  return (
    <section
      className={cn(
        'flex w-full flex-col',
        props.hidePadding ? GAP_MAP[size] : SIZE_MAP[size],
        props.fullHeight && 'h-full',
        // Without a background there is no corner to round, and clipping would only
        // shave the shadows and focus rings of whatever the card wraps
        !props.hideBackground &&
          `overflow-clip rounded-xl border border-gray-200 ${VARIANT_MAP[props.variant ?? 'base']} shadow-sm`,
      )}
    >
      <CollapseContext.Provider value={{ showBody, setShowBody, bodyId }}>
        {props.children}
      </CollapseContext.Provider>
    </section>
  );
}

function Header(props: CardHeaderProps) {
  return (
    <div className="flex w-full shrink-0 items-center justify-between">
      <Heading style="sm" level={props.headingLevel} icon={props.icon} tooltip={props.tooltip}>
        {props.children}
      </Heading>
      <div className="flex shrink-0 items-center gap-2 self-start">
        <CardBadges badges={props.badges} />
        {props.primaryAction &&
          (props.primaryAction.popoverContent ? (
            <Popover className="relative">
              <PopoverButton
                aria-label={props.primaryAction.ariaLabel}
                disabled={props.primaryAction.disabled}
                className="flex h-spark-component shrink-0 items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 text-sm shadow-xs transition hover:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-spark-purple focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {props.primaryAction.icon && <Icon name={props.primaryAction.icon} size="sm" />}
                {props.primaryAction.label}
              </PopoverButton>
              <Transition
                enter="transform transition duration-100 ease-out"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="transform transition duration-100 ease-in"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <PopoverPanel
                  anchor="bottom end"
                  className="z-30 mt-1 min-w-48 rounded-md border border-gray-300 bg-white p-3 shadow-lg"
                >
                  {props.primaryAction.popoverContent}
                </PopoverPanel>
              </Transition>
            </Popover>
          ) : (
            <Button
              variant={props.primaryAction.variant ?? 'secondary'}
              icon={props.primaryAction.icon}
              ariaLabel={props.primaryAction.ariaLabel}
              onClick={props.primaryAction.onClick}
              disabled={props.primaryAction.disabled}
              loading={props.primaryAction.loading}
            >
              {props.primaryAction.label}
            </Button>
          ))}
        {props.collapsible && <CollapseToggle />}
      </div>
    </div>
  );
}

function Body(props: CardBodyProps) {
  const { showBody, bodyId } = useContext(CollapseContext);

  if (!showBody) {
    return null;
  }

  return (
    <div
      id={bodyId}
      className={cn('flex w-full grow flex-col gap-3', props.justifyBetween && 'justify-between')}
    >
      {props.children}
    </div>
  );
}

Card.Header = Header;
Card.Body = Body;
